import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { TagType } from './TagsManagement';
interface TagTypeFormProps {
  tagType: TagType | null;
  onSave: (tagType: Omit<TagType, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  locale: string;
}
export function TagTypeForm({
  tagType,
  onSave,
  onCancel,
  locale
}: TagTypeFormProps) {
  const [formData, setFormData] = useState<Omit<TagType, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    description: '',
    isRestricted: false,
    colourPalette: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444' // Red
    ]
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [newColor, setNewColor] = useState('#6366F1'); // Default new color (Indigo)
  // Initialize form data when editing a tag type
  useEffect(() => {
    if (tagType) {
      setFormData({
        name: tagType.name,
        description: tagType.description,
        isRestricted: tagType.isRestricted,
        colourPalette: tagType.colourPalette || []
      });
    }
  }, [tagType]);
  // Handle form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handle adding a color to the palette
  const handleAddColor = () => {
    if (newColor && (!formData.colourPalette || !formData.colourPalette.includes(newColor))) {
      setFormData({
        ...formData,
        colourPalette: [...(formData.colourPalette || []), newColor]
      });
    }
  };
  // Handle removing a color from the palette
  const handleRemoveColor = (colorToRemove: string) => {
    setFormData({
      ...formData,
      colourPalette: formData.colourPalette?.filter(color => color !== colorToRemove) || []
    });
  };
  // Validate form before submission
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Tag type name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17923">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-17924">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17925">
          {tagType ? 'Edit Tag Type' : 'Create Tag Type'}
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-17926">
          {tagType ? 'Update the details of an existing tag type' : 'Define a new tag type to categorize tags'}
        </p>
      </div>
      <form onSubmit={handleSubmit} data-id="element-17927">
        <div className="px-4 py-5 sm:p-6" data-id="element-17928">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-17929">
            <div className="sm:col-span-4" data-id="element-17930">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-17931">
                Tag Type Name <span className="text-red-500" data-id="element-17932">*</span>
              </label>
              <div className="mt-1" data-id="element-17933">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} placeholder="e.g., Priority, Project, Department" data-id="element-17934" />
                {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-17935">{errors.name}</p>}
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-17936">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-17937">
                Description
              </label>
              <div className="mt-1" data-id="element-17938">
                <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Optional description of the tag type" data-id="element-17939" />
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-17940">
              <div className="flex items-start" data-id="element-17941">
                <div className="flex items-center h-5" data-id="element-17942">
                  <input id="isRestricted" name="isRestricted" type="checkbox" checked={formData.isRestricted} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-17943" />
                </div>
                <div className="ml-3 text-sm" data-id="element-17944">
                  <label htmlFor="isRestricted" className="font-medium text-gray-700" data-id="element-17945">
                    Restricted Tag Type
                  </label>
                  <p className="text-gray-500" data-id="element-17946">
                    When checked, only predefined tag values will be allowed for
                    this tag type
                  </p>
                </div>
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-17947">
              <label className="block text-sm font-medium text-gray-700" data-id="element-17948">
                Color Palette
              </label>
              <p className="mt-1 text-sm text-gray-500" data-id="element-17949">
                Define the colors that can be used for tags of this type
              </p>
              <div className="mt-2" data-id="element-17950">
                <div className="flex flex-wrap gap-2 mb-4" data-id="element-17951">
                  {formData.colourPalette && formData.colourPalette.map((color, index) => <div key={index} className="flex items-center space-x-1 bg-gray-100 rounded-md p-1" data-id="element-17952">
                        <div className="h-6 w-6 rounded-md" style={{
                    backgroundColor: color
                  }} data-id="element-17953" />
                        <span className="text-xs text-gray-700" data-id="element-17954">{color}</span>
                        <button type="button" onClick={() => handleRemoveColor(color)} className="text-gray-400 hover:text-red-500" data-id="element-17955">
                          <TrashIcon className="h-4 w-4" data-id="element-17956" />
                        </button>
                      </div>)}
                </div>
                <div className="flex items-center space-x-2" data-id="element-17957">
                  <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} className="h-8 w-8 rounded-md border-gray-300" data-id="element-17958" />
                  <input type="text" value={newColor} onChange={e => setNewColor(e.target.value)} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-24 sm:text-sm border-gray-300 rounded-md" placeholder="#RRGGBB" data-id="element-17959" />
                  <button type="button" onClick={handleAddColor} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17960">
                    <PlusIcon className="h-3 w-3 mr-1" data-id="element-17961" />
                    Add Color
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-17962">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17963">
            <XIcon className="h-4 w-4 mr-1.5" data-id="element-17964" />
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17965">
            <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-17966" />
            {tagType ? 'Update Tag Type' : 'Create Tag Type'}
          </button>
        </div>
      </form>
    </div>;
}